import React, { useEffect, useMemo, useState } from 'react';
import ToggleSensitiveDataButton, { ToggleSensitiveDataButtonProps } from './buttons/ToggleSensitiveDataButton';
import { useListContext } from 'framework/list';
import useSensitiveDataService from './sensitiveDataService';
import { SensitiveDataContextProvider } from './SensitiveDataContext';

type SensitiveData = Dictionary<any>;

const SensitiveListElementLayout = ({
  children,
  sensitiveDataIdSource,
  loadSensitiveDataMethod,
  timerId,
  getSensitiveDataIds,
  shouldShowSensitiveDataButton,
  isSensitiveDataIsShown = true,
  toggleSensitiveDataComponent: ToggleSensitiveDataComponent = ToggleSensitiveDataButton,
}: {
  sensitiveDataIdSource: string;
  children: React.ReactElement;
  loadSensitiveDataMethod: (ids: string[]) => Promise<SensitiveData>;
  timerId?: string;
  getSensitiveDataIds?: (items?: Dictionary<any>[]) => string[];
  shouldShowSensitiveDataButton?: boolean;
  isSensitiveDataIsShown?: boolean;
  toggleSensitiveDataComponent?: React.FC<ToggleSensitiveDataButtonProps>;
}) => {
  const [sensitiveDataIds, setSensitiveDataIds] = useState([]);
  const { data: { items } = { items: [] } } = useListContext();

  const {
    isTimerActive,
    isShown,
    toggleSensitiveData,
    getValueByIdAndSource,
    isSensitiveDataLoading,
    loadSensitiveDataByIds,
    sensitiveData,
  } = useSensitiveDataService({
    loadSensitiveDataMethod,
    timerId,
  });

  useEffect(() => {
    if (getSensitiveDataIds) {
      setSensitiveDataIds(getSensitiveDataIds(items) as any);

      return;
    }

    if (items?.length) {
      const sensitiveIds = items?.map((item: any) => item[sensitiveDataIdSource]);

      setSensitiveDataIds(sensitiveIds as any);
    }
  }, [items, sensitiveDataIdSource, getSensitiveDataIds]);

  const hasSensitiveData = useMemo(() => sensitiveData && Object.keys(sensitiveData).length, [sensitiveData]);

  const isShownWithoutSensitiveData = useMemo(
    () => sensitiveDataIds?.length && !hasSensitiveData && isShown,
    [isShown, hasSensitiveData, sensitiveDataIds],
  );

  useEffect(() => {
    if (isShownWithoutSensitiveData) {
      loadSensitiveDataByIds(sensitiveDataIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShownWithoutSensitiveData]);

  // Effect to load sensitive data when pagination occurs and isShown is true
  useEffect(() => {
    if (isShown && sensitiveDataIds?.length > 0) {
      const hasMissingData = sensitiveDataIds.some(id => !sensitiveData || !sensitiveData[id]);

      if (hasMissingData) {
        loadSensitiveDataByIds(sensitiveDataIds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isShown, sensitiveDataIds]);

  return (
    <SensitiveDataContextProvider
      value={{
        sensitiveDataIdSource,
        getValueByIdAndSource,
      }}
    >
      {React.cloneElement(children, {
        isSensitiveDataIsShown:
          (isShown && !isSensitiveDataLoading && hasSensitiveData) || (!items?.length && isSensitiveDataIsShown),
        toggleSensitiveDataButton:
          items?.length || shouldShowSensitiveDataButton ? (
            <ToggleSensitiveDataComponent
              key="toggle-sensitive-data"
              isShown={isShown}
              onClick={() => toggleSensitiveData(sensitiveDataIds)}
              disabled={isSensitiveDataLoading}
              isTimerActive={isTimerActive}
            />
          ) : null,
        reloadSensitiveData: () => {
          const ids = getSensitiveDataIds
            ? getSensitiveDataIds(items)
            : items?.map((item: any) => item[sensitiveDataIdSource]);

          loadSensitiveDataByIds(ids || []);
        },
      })}
    </SensitiveDataContextProvider>
  );
};

export default SensitiveListElementLayout;
